<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    agreeText="dialog.downloadChart"
    dialogWidth="55%"
    header-shadow
    body-height="600px"
    :is-agree-visible="isSingle"
    :title="this.$i18n.t('sidebar.zdmLightingControl.lightingHeadHistoricalData')"
    @agree="downloadHistoricalDataChart"
    no-paddings-x
  >
    <template #body>
      <v-col cols="12">
        <v-form v-model="isValid">
          <v-row no-gutters class="px-5">
            <v-col cols="auto" class="pt-2">
              <v-icon class="pr-1" color="rgba(0,0,0,0.7)">mdi-calendar</v-icon>
            </v-col>
            <v-col>
              <v-row no-gutters>
                <v-col cols="4">
                  <data-input v-model="dateRange" v-bind="dateRangeInput" />
                </v-col>
                <v-col cols="4" class="pl-4" v-if="isSingle">
                  <data-input v-model="currentParameter" v-bind="currentParameterInput" />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
        <v-row no-gutters align="center" justify="center" class="pt-10">
          <template v-if="statsFetched">
            <v-row no-gutters class="justify-center align-center" v-if="isSingle">
              <v-col cols="9" class="fill-height chart">
                <canvas id="headChart" />
              </v-col>
            </v-row>
            <v-row no-gutters class="justify-center align-center" v-else>
              <v-col cols="12" class="fill-height chart pa-0 ma-0">
                <v-data-table
                  v-bind="tableProps"
                  :items="currentStats.map(stat => ({ ...stat, measurement_unit: stat['measurement_unit'] || '-' }))"
                />
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <main-loader v-if="statsLoading" />
          </template>
        </v-row>
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import Chart from 'chart.js';
import { debounce } from 'lodash';

import { call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import MainLargeDialog from '@/components/MainLargeDialog';
export default {
  name: 'ZdmLightingHeadHistoricalDataModal',
  components: {
    DataInput,
    MainLargeDialog,
    MainLoader,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    isSingle: {
      type: Boolean,
      default: false,
    },
    headsIds: {
      type: Array,
      required: true,
    },
    parameters: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  computed: {
    tableProps() {
      return {
        hideDefaultFooter: true,
        disablePagination: true,
        headers: [
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.name'),
            value: 'verbose_name',
            align: 'center',
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.sumValue'),
            value: 'value',
            align: 'center',
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.unit'),
            value: 'measurement_unit',
            align: 'center',
          },
        ],
      };
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    currentParameterInput() {
      return {
        dataType: { name: 'select' },
        hideDetails: true,
        dense: true,
        items: this.parameters,
        label: this.$i18n.t('sidebar.zdmLightingControl.parameter'),
        disabled: this.statsLoading,
      };
    },
    dateRanges() {
      const dict = {
        today: this.$dayjs(),
        tomorrow: this.$dayjs().add(1, 'd'),
        yesterday: this.$dayjs().subtract(1, 'd'),
        dayBeforeYesterday: this.$dayjs().subtract(2, 'd'),
        currentWeekStart: this.$dayjs().startOf('w'),
        currentWeekEnd: this.$dayjs().endOf('w'),
        currentMonthStart: this.$dayjs().startOf('M'),
        currentMonthEnd: this.$dayjs().endOf('M'),
        currentYearStart: this.$dayjs().startOf('y'),
        currentYearEnd: this.$dayjs().endOf('y'),
      };

      Object.keys(dict).map(key => {
        dict[key] = dict[key].format('YYYY-MM-DD');
      });
      return dict;
    },
    dateRangeInput() {
      return {
        dataType: { name: 'daterange' },
        hideDetails: true,
        label: this.$i18n.t('sidebar.zdmLightingControl.dateRangeFromTo'),
        disabled: this.statsLoading,
        returnType: 'date',
        defaultRanges: [
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.today'),
            value: [this.dateRanges.today, this.dateRanges.tomorrow],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.yesterday'),
            value: [this.dateRanges.yesterday, this.dateRanges.today],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisWeek'),
            value: [this.dateRanges.currentWeekStart, this.dateRanges.currentWeekEnd],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisMonth'),
            value: [this.dateRanges.currentMonthStart, this.dateRanges.currentMonthEnd],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisYear'),
            value: [this.dateRanges.currentYearStart, this.dateRanges.currentYearEnd],
          },
        ],
      };
    },
  },
  data: () => ({
    currentParameter: '',
    currentStats: undefined,
    dateRange: [],
    headChart: undefined,
    isValid: false,
    statsLoading: false,
    statsFetched: false,
  }),
  methods: {
    getHeadStats: call('lightingControl/getHeadStats'),
    getHeadsStats: call('lightingControl/getHeadsStats'),
    downloadHistoricalDataChart() {
      this.$_saveFile(this.headChart.toBase64Image(), this.$i18n.t('dialog.body.chart'));
    },
    drawHeadChart() {
      Chart.plugins.register({
        afterRender: function(c) {
          const ctx = c.chart.ctx;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, c.chart.width, c.chart.height);
          ctx.restore();
        },
      });
      const labels = [];
      const data = [];
      for (let stat of this.currentStats) {
        const [date, time] = stat['timestamp'].split('T');
        const dateFormated = this.$dayjs(date).format('L');
        const timeFormated = time.substring(0, time.length - 3);
        labels.push(`${dateFormated} ${timeFormated}`);
        data.push(stat['value']);
      }
      if (this.headChart) {
        this.headChart.destroy();
      }
      const canvas = document.getElementById('headChart');
      const currentParameterData = this.parameters.find(param => param.value === this.currentParameter);
      this.headChart = new Chart(canvas, {
        options: {
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            display: false,
          },
          elements: {
            point: {
              radius: 3,
            },
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  autoSkip: true,
                  stepSize: 20,
                },
                scaleLabel: {
                  display: true,
                  labelString: currentParameterData.text,
                },
              },
            ],
            xAxes: [
              {
                ticks: {
                  autoSkip: true,
                },
              },
            ],
          },
        },
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              data: data,
              fill: false,
              lineTension: 0,
              borderColor: '#2a579a',
              backgroundColor: '#2a579a',
            },
          ],
        },
      });
    },
    async prepareStats() {
      if (this.dateRange.length !== 2 || (!this.currentParameter && this.isSingle) || !this.isValid) {
        return;
      }
      this.statsLoading = true;
      this.statsFetched = false;
      try {
        let stats = '';
        const [dateStart, dateEnd] = this.dateRange;
        const payload = {
          date_from: dateStart,
          date_to: dateEnd,
        };
        if (this.isSingle) {
          payload.parameter = this.currentParameter;
          stats = await this.getHeadStats({
            headId: this.headsIds[0],
            payload,
          });
        } else {
          payload.heads_ids = this.headsIds;
          stats = await this.getHeadsStats(payload);
          console.log(stats);
        }
        this.currentStats = stats;
        this.statsFetched = true;
        this.statsLoading = false;
        setTimeout(() => {
          if (this.isSingle) {
            this.drawHeadChart();
          }
        });
      } catch {
        this.statsFetched = false;
        this.statsLoading = false;
      }
    },
  },
  watch: {
    computedIsVisible(nV) {
      if (!nV) {
        this.currentParameter = '';
        this.dateRange = [];
        this.statsFetched = false;
        this.statsLoading = false;
      }
    },
    dateRange() {
      this.prepareStats();
    },
    currentParameter() {
      this.prepareStats();
    },
  },
  created() {
    this.prepareStats = debounce(this.prepareStats, 1000);
  },
};
</script>
